// Our main CSS
import '../css/app.css'
import Splide from '@splidejs/splide'
import '@splidejs/splide/dist/css/splide-core.min.css'
import gsap from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
gsap.registerPlugin(ScrollToPlugin)

/**
 * Vue is ready for you, if you need it.
 */

/**
 * The following block of code automatically registers your Vue components.
 * It will recursively scan this directory for the Vue components and
 * automatically register them with their "basename".
 *
 * Eg. ./components/ExampleComponent.vue -> <example-component></example-component>
 *
 * Feel free to remove this block if you don't like black magic fuckery,
 * or you want to register your Vue components manually.
 */
/*
 *** https://v3.vuejs.org/
 */
// interface ImportedModule {
//   name: string
//   default: object
// }
// const vueSingleFileComponents = import.meta.glob<ImportedModule>('./**/*.vue', {
//   eager: true
// })

// import { createApp } from 'vue'
// const app = createApp({})

// for (const path in vueSingleFileComponents) {
//   const importedModule = vueSingleFileComponents[String(path)]
//   const name = path.split('/').pop()?.split('.')[0]
//   if (name !== undefined && typeof name === 'string') {
//     app.component(name, importedModule.default)
//   }
// }
// app.mount('#vue-app')

/**
OR:

Load a js-module dynamically, if the parent element exists, in this example, a vue component. (The js-file wont load if html element with id #ExampleVueComponentWrapper does not exists)
*/

/**
const ExampleVueComponentWrapper = document.getElementById(
  'ExampleVueComponentWrapper'
)
if (ExampleVueComponentWrapper && ExampleVueComponentWrapper !== null) {
  import('./ExampleVueComponent/index.js')
    .then((module) => {
      module.default(ExampleVueComponentWrapper as HTMLElement)
    })
    .catch((err) => {
      console.log(err)
      // main.textContent = err.message;
    })
}
*/

/**
 * 🦄: Now do your magic.
 */

const initNonCritical = async () => {
  // Lazy load video handling
  const autoplaying_videos = document.querySelectorAll<HTMLVideoElement>('video[playsinline]')
  if (autoplaying_videos?.length) {
    import('./modules/inline-videos').then(({ default: handleVideos }) => {
      handleVideos(autoplaying_videos)
    })
  }

  /*
    Intersecting elements helper
*/
  const check_intersecting = document.querySelectorAll(
    '[data-check-intersecting]'
  ) as NodeListOf<HTMLElement>

  if (check_intersecting?.length) {
    import('./modules/check-intersection').then(({ default: init }) => {
      init(check_intersecting)
    })
  }
}

if ('requestIdleCallback' in window) {
  requestIdleCallback(() => initNonCritical())
} else {
  // Fallback for browsers that don't support requestIdleCallback
  setTimeout(initNonCritical, 1)
}

/* 
Vue Accordion Component 
*/
const AccordionComponentWrappers = document.querySelectorAll('.AccordionComponentWrapper')
if (AccordionComponentWrappers && AccordionComponentWrappers.length) {
  AccordionComponentWrappers.forEach((wrapper) => {
    import('./AccordionComponent/index.ts')
      .then((module) => {
        module.default(wrapper as HTMLElement)
      })
      .catch((err) => {
        console.log(err)
        // main.textContent = err.message;
      })
  })
}

/* 
Vue Hero Block Component 
*/
const HeroBlockWrapper = document.getElementById('HeroBlockWrapper')
if (HeroBlockWrapper && HeroBlockWrapper !== null) {
  import('./HeroBlock/index.js')
    .then((module) => {
      module.default(HeroBlockWrapper as HTMLElement)
    })
    .catch((err) => {
      console.log(err)
      // main.textContent = err.message;
    })
}

/* 
Footer Word Animation 
*/
document.addEventListener('DOMContentLoaded', () => {
  const list = document.getElementById('footer-word-list')
  if (!list) {
    return
  }

  let words = list.querySelectorAll('span')
  if (!words.length) {
    return
  }

  if (words[0]) {
    const clone = words[0].cloneNode(true)
    list.appendChild(clone)
  }

  words = list.querySelectorAll('span')
  const wordCount = words.length

  let wordHeight = words[0] ? words[0].offsetHeight : 0
  let index = 0
  let resizeTimeout: number | null = null

  function updateWordHeight() {
    if (words[0]) {
      wordHeight = words[0].offsetHeight
      gsap.set(list, { y: wordHeight * index })
    }
  }

  function throttleResize() {
    if (resizeTimeout) {
      cancelAnimationFrame(resizeTimeout)
    }
    resizeTimeout = requestAnimationFrame(updateWordHeight)
  }

  if (document.fonts) {
    document.fonts.ready.then(updateWordHeight)
  }

  window.addEventListener('resize', throttleResize)

  function rollWords() {
    index++

    gsap.to(list, {
      y: wordHeight * index,
      duration: 0.7,
      ease: 'power2.inOut',
      onComplete: () => {
        if (index >= wordCount - 1) {
          gsap.set(list, { y: 0 })
          index = 0
        }
      }
    })
  }

  setInterval(rollWords, 2000)
})

/* 
Dynamic Border Radius based on height 
*/
document.addEventListener('DOMContentLoaded', () => {
  const elements = document.querySelectorAll('[data-dynamic-radius]')

  elements.forEach((el) => {
    const element = el as HTMLElement
    const factor = parseFloat(element.dataset.radiusFactor || '') || 0.1
    const minWidth = element.dataset.minWidth ? parseInt(element.dataset.minWidth) : null

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height

        if (minWidth) {
          element.style.borderRadius =
            window.innerWidth >= minWidth ? `${height * factor}px` : '0px'
        } else {
          element.style.borderRadius = `${height * factor}px`
        }
      }
    })

    observer.observe(element)
  })
})

/*
Splide Carousel Initialization
*/
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll<HTMLElement>('.card-slider').forEach((el) => {
    new Splide(el, {
      perPage: 1.9,
      perMove: 1,
      flickMaxPages: 1,
      focus: 0,
      arrows: false,
      pagination: true,
      omitEnd: true,
      breakpoints: {
        1440: {
          perPage: 1.5
        },
        1024: {
          perPage: 1.1
        }
      }
    })
      .on('pagination:mounted', function (data) {
        data.items.forEach(function (item) {
          const span = document.createElement('span')
          span.classList.add('pagination-indicator')
          item.button.appendChild(span)
        })
      })
      .mount()
  })
})

/*
Menu Setup
*/
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll<HTMLElement>('section[id]')
  const internalLinks = document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]:not([href="#"])')
  const toggle = document.getElementById('mobile-menu-toggle')
  const menu = document.getElementById('mobile-menu')
  const lastScrollY = window.scrollY

  function closeMenu() {
    if (toggle && menu && toggle.classList.contains('open')) {
      toggle.classList.remove('open')
      toggle.setAttribute('aria-expanded', 'false')
      menu.style.maxHeight = '0'
      menu.classList.add('opacity-0')
      menu.classList.remove('opacity-100')
    }
  }

  internalLinks.forEach((link) => {
    link.addEventListener('click', () => {
      closeMenu()
    })
  })

  const intersectingSections = new Set<string>()

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const id = entry.target.getAttribute('id')
        if (!id) {return}
        if (entry.isIntersecting) {
          intersectingSections.add(id)
        } else {
          intersectingSections.delete(id)
        }
      })

      const activeId =
        Array.from(sections).find((s) => s.id && intersectingSections.has(s.id))?.id ?? null

      sections.forEach((section) => {
        const id = section.id
        if (!id) {return}
        const links = document.querySelectorAll<HTMLAnchorElement>(
          `#main-menu .menu-link[href="#${id}"], #mobile-menu .mobile-menu-link[href="#${id}"]`
        )
        links.forEach((link) => {
          link.classList.toggle('active', id === activeId)
        })
      })
    },
    { root: null, threshold: 0.2, rootMargin: '-140px 0px -140px 0px' }
  )

  sections.forEach((section) => observer.observe(section))

  if (toggle && menu) {
    toggle.addEventListener('click', () => {
      const isOpen = toggle.classList.toggle('open')
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')

      if (isOpen) {
        menu.style.maxHeight = menu.scrollHeight + 'px'
        setTimeout(() => {
          menu.classList.add('opacity-100')
          menu.classList.remove('opacity-0')
        }, 50)
      } else {
        menu.style.maxHeight = '0'
        menu.classList.add('opacity-0')
        menu.classList.remove('opacity-100')
      }
    })

    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1536) {
        toggle.classList.remove('open')
        toggle.setAttribute('aria-expanded', 'false')
        menu.style.maxHeight = '0'
        menu.classList.add('opacity-0')
        menu.classList.remove('opacity-100')
      }
    })
  }

  window.addEventListener('scroll', () => {
    if (window.innerWidth < 1536 && toggle?.classList.contains('open')) {
      if (Math.abs(window.scrollY - lastScrollY) > 50) {
        closeMenu()
      }
    }
  })
})
